import { Float } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { loadAndAssert } from '../authz/access.js';
import { Exam } from '../exam.js';

export const updateUploadProgressMutation = registerMutation({
  operationName: 'updateUploadProgressMutation',
  type: () => Boolean,
  params: [
    { name: 'examId', type: () => String },
    { name: 'totalFiles', type: () => Float },
  ],
  resolve: (examId: string, totalFiles: number) => async () => {
    await loadAndAssert(Exam, examId, 'update', { _id: 1 });
    const examCollection = await getCollection(Exam);

    // Get existing exam to check if uploadProgress already exists
    const existingExam = await examCollection.findOne({ _id: examId });
    if (!existingExam) {
      throw new Error(`Klausur mit ID ${examId} nicht gefunden.`);
    }

    const existingUploadProgress = existingExam.examState?.uploadProgress;

    // Determine the new values
    const newTotalFiles = existingUploadProgress ? existingUploadProgress.totalFiles + totalFiles : totalFiles;
    const uploadedFiles = existingUploadProgress ? existingUploadProgress.uploadedFiles + totalFiles : totalFiles;

    const result = await examCollection.updateOne(
      { _id: examId },
      {
        $set: {
          'examState.uploadProgress': {
            _class: 'UploadProgess',
            totalFiles: newTotalFiles,
            uploadedFiles,
          },
        },
      },
    );

    if (result.matchedCount === 0) {
      throw new Error(`Klausur mit ID ${examId} nicht gefunden.`);
    }

    return true;
  },
});

export const updateSubmissionsFoundMutation = registerMutation({
  operationName: 'updateSubmissionsFoundMutation',
  type: () => Boolean,
  params: [
    { name: 'examId', type: () => String },
    { name: 'submissionsFound', type: () => Float },
  ],
  resolve: (examId: string, submissionsFound: number) => async () => {
    await loadAndAssert(Exam, examId, 'update', { _id: 1 });
    const examCollection = await getCollection(Exam);

    const existingExam = await examCollection.findOne({ _id: examId });
    if (!existingExam) {
      throw new Error(`Klausur mit ID ${examId} nicht gefunden.`);
    }

    const current = existingExam?.examState?.uploadProgress?.submissionsFound;

    let result: any;
    if (typeof current === 'number') {
      // If already initialized, increment by the provided amount
      result = await examCollection.updateOne(
        { _id: examId },
        { $inc: { 'examState.uploadProgress.submissionsFound': submissionsFound } },
      );
    } else {
      // Not initialized yet — set to the provided value
      result = await examCollection.updateOne(
        { _id: examId },
        { $set: { 'examState.uploadProgress.submissionsFound': submissionsFound } },
      );
    }

    if (result.matchedCount === 0) {
      throw new Error(`Klausur mit ID ${examId} nicht gefunden.`);
    }

    return true;
  },
});

export const incrementUploadedFilesMutation = registerMutation({
  operationName: 'incrementUploadedFilesMutation',
  type: () => Boolean,
  params: [{ name: 'examId', type: () => String }],
  resolve: (examId: string) => async () => {
    await loadAndAssert(Exam, examId, 'update', { _id: 1 });
    const examCollection = await getCollection(Exam);

    const result = await examCollection.updateOne(
      { _id: examId },
      { $inc: { 'examState.uploadProgress.uploadedFiles': 1 } },
    );

    if (result.matchedCount === 0) {
      throw new Error(`Klausur mit ID ${examId} nicht gefunden.`);
    }

    return true;
  },
});

export const decrementUploadProgressMutation = registerMutation({
  operationName: 'decrementUploadProgressMutation',
  type: () => Boolean,
  params: [
    { name: 'examId', type: () => String },
    { name: 'fileCount', type: () => Float },
  ],
  resolve: (examId: string, fileCount: number) => async () => {
    await loadAndAssert(Exam, examId, 'update', { _id: 1 });
    const examCollection = await getCollection(Exam);

    const normalizedCount = Number(fileCount);
    const delta = Number.isFinite(normalizedCount) ? Math.max(0, normalizedCount) : 0;
    if (delta <= 0) return true;

    const existingExam = await examCollection.findOne(
      { _id: examId },
      { projection: { 'examState.uploadProgress': 1 } },
    );
    const currentProgress = (existingExam as any)?.examState?.uploadProgress ?? {};
    const currentTotalFiles = Number(currentProgress.totalFiles ?? 0);
    const currentUploadedFiles = Number(currentProgress.uploadedFiles ?? 0);

    const nextTotalFiles = Math.max(0, currentTotalFiles - delta);
    const nextUploadedFiles = Math.max(0, currentUploadedFiles - delta);

    const result = await examCollection.updateOne(
      { _id: examId },
      {
        $set: {
          'examState.uploadProgress': {
            _class: 'UploadProgess',
            totalFiles: nextTotalFiles,
            uploadedFiles: nextUploadedFiles,
          },
        },
      },
    );

    if (result.matchedCount === 0) {
      throw new Error(`Klausur mit ID ${examId} nicht gefunden.`);
    }

    return true;
  },
});

export const setExamUiNeedsRefreshMutation = registerMutation({
  operationName: 'setExamUiNeedsRefreshMutation',
  type: () => Boolean,
  params: [
    { name: 'examId', type: () => String },
    { name: 'uiNeedsRefresh', type: () => Boolean },
  ],
  resolve: (examId: string, uiNeedsRefresh: boolean) => async () => {
    await loadAndAssert(Exam, examId, 'update', { _id: 1 });
    const examCollection = await getCollection(Exam);

    const result = await examCollection.updateOne(
      { _id: examId },
      {
        $set: {
          'examState.uiNeedsRefresh': uiNeedsRefresh,
        },
      },
    );

    if (result.matchedCount === 0) {
      throw new Error(`Klausur mit ID ${examId} nicht gefunden.`);
    }

    return true;
  },
});
